import type { LocaleOption } from '../types/locale';
import type { UserRole } from '../types/user-identity';

/*
|--------------------------------------------------------------------------
| LOCALE CONSTANTS
|--------------------------------------------------------------------------
*/

/** Standard agency / AR locales available to all non-importer users. */
export const LOCALES: LocaleOption[] = [
  { value: 'de-de', label: 'Deutschland - Deutsch', group: 'Europe' },
  { value: 'en-gb', label: 'United Kingdom - English', group: 'Europe' },
  { value: 'nl-nl', label: 'Nederland - Nederlands', group: 'Europe' },
  { value: 'en-ca', label: 'Canada - English', group: 'Americas' },
  { value: 'fr-ca', label: 'Canada - Français', group: 'Americas' },
  { value: 'en-us', label: 'United States - English', group: 'Americas' },
];

/** Locales available to importer-role users (Belgium + Denmark markets). */
export const IMPORTER_LOCALES: LocaleOption[] = [
  { value: 'en-be', label: 'Belgium - English', group: 'Europe' },
  { value: 'fr-be', label: 'Belgique - Français', group: 'Europe' },
  { value: 'nl-be', label: 'België - Nederlands', group: 'Europe' },
  { value: 'de-be', label: 'Belgien - Deutsch', group: 'Europe' },
  { value: 'da-dk', label: 'Danmark - Dansk', group: 'Europe' },
];

/** Fast-lookup Set of importer locale values. */
export const IMPORTER_LOCALE_VALUES: ReadonlySet<string> = new Set(
  IMPORTER_LOCALES.map((l) => l.value)
);

/** Default locale applied to importer users when no stored locale is present. */
export const IMPORTER_DEFAULT_LOCALE = 'en-be';

/** Locales available to Uber-role users (US market only). */
export const UBER_LOCALES: LocaleOption[] = [
  { value: 'en-us', label: 'United States - English', group: 'Americas' },
];

/** Fast-lookup Set of Uber locale values. */
export const UBER_LOCALE_VALUES: ReadonlySet<string> = new Set(UBER_LOCALES.map((l) => l.value));

/** Default (and only) locale applied to Uber users. */
export const UBER_DEFAULT_LOCALE = 'en-us';

/**
 * Returns the locales available to the given user role.
 *
 * Importers (`role.marketType === 'Importer'`) receive the Belgium/Denmark locale set.
 * Uber (`role.marketType === 'Uber'`) is locked to en-us.
 * All other roles receive the standard agency/AR locale list.
 */
export const filterLocalesByRole = (role: UserRole | null): LocaleOption[] => {
  if (!role) return [];
  if (role.marketType === 'Importer') return IMPORTER_LOCALES;
  if (role.marketType === 'Uber') return UBER_LOCALES;
  return LOCALES;
};

/*
|--------------------------------------------------------------------------
| LOCALE UTILITIES
|--------------------------------------------------------------------------
*/

/**
 * Convert a locale string (e.g., 'en-us', 'de-de') to Contentful locale format (e.g., 'en-US', 'de-DE').
 */
export const convertToContentfulLocale = (locale: string): string => {
  if (!locale || !locale.includes('-')) return 'de-DE';
  const [lang, country] = locale.split('-');
  return `${lang}-${country.toUpperCase()}`;
};

/**
 * Map a language code (e.g., 'en', 'de') to the corresponding Contentful locale string.
 */
export const getContentfulLocaleFromLanguage = (languageCode: string): string => {
  const map: Record<string, string> = {
    en: 'en-US',
    de: 'de-DE',
    fr: 'fr-FR',
    nl: 'nl-NL',
    nb: 'nb-NO',
    ar: 'ar-SA',
  };
  return map[languageCode.toLowerCase()] ?? 'en-US';
};

/**
 * Build a locale-aware path by prepending the locale extracted from the current path.
 * If the new path already has a locale prefix or is an absolute URL it is returned as-is.
 */
export const buildLocalePath = (currentPath: string, newPath: string): string => {
  if (newPath.startsWith('http') || /^\/[a-z]{2}-[a-z]{2}\//i.test(newPath)) {
    return newPath;
  }
  const match = currentPath.match(/^\/([a-z]{2}-[a-z]{2})\//i);
  if (match) {
    return `/${match[1]}${newPath}`;
  }
  return newPath;
};
